/**
 * asset-feeds.ts — gather installable assets from every feed the manage TUI
 * surfaces.
 *
 * Four feeds, normalized into `CatalogEntry`:
 * - **Project sources** — `skaile.yaml` `sources:` entries (scanned via
 *   `AssetManager.search()`; the new `sources:` field is merged into the
 *   legacy repositories map in `AssetManager.loadConfig`).
 * - **Local libraries** — user authoring places from `LocalIndex` (filesystem
 *   scan of each library's `path`).
 * - **Factory assets** — the bundled first-party `factory-assets` tree
 *   (`factoryAssetRoots()`), scanned from disk. Always present; no network.
 * - **Remote store** — the configured catalog source when `catalog.url` is
 *   not `local`. Failures are non-fatal — they surface as notes.
 */

import { existsSync, readFileSync } from "node:fs";
import * as path from "node:path";
import {
  type AssetKind,
  type CatalogDomain,
  type CatalogEntry,
  factoryAssetRoots,
  parseFrontmatter,
  scanRepo,
  walkDir,
} from "@skaile/workspaces/core";
import {
  assignDomain,
  buildDomainIndex,
  DOMAIN_MD_FILENAME,
  type DomainMd,
  parseDomainMd,
} from "@skaile/workspaces/discovery";

/** One non-fatal note returned to the caller for surfacing in the UI. */
export interface FeedNote {
  feed: "sources" | "libraries" | "store" | "factory";
  message: string;
}

/**
 * Which feed an asset came from. This is the top-level grouping axis in the
 * manage TUI ("where is this skill coming from?") and is intentionally distinct
 * from `publisher` (asset-intrinsic identity): the same publisher can legitimately
 * appear under more than one origin (e.g. a github repo you added as a `source`
 * that is also listed in the remote `store`).
 */
export type AssetOrigin = "source" | "library" | "factory" | "store";

/** A catalog entry tagged with the feed it was gathered from. */
export interface OriginEntry {
  origin: AssetOrigin;
  entry: CatalogEntry;
  /**
   * Second-level grouping label for the manage tree. Defaults (when unset) to the
   * entry's `publisher`; the source feed sets it to the source's short repo name
   * so the Sources band groups by source — "which repo did this come from" — not
   * by the asset-intrinsic publisher.
   */
  group?: string;
}

export interface AssetFeedResult {
  entries: OriginEntry[];
  /**
   * Display-only domain metadata, keyed (by the consumer) on `slug`. Populated
   * from the store's `catalog.listDomains` when the connected catalog source
   * exposes it; empty otherwise. The manage TUI joins `entry.domainSlug` → a
   * domain for grouping headers, descriptions, and relation badges.
   */
  domains: CatalogDomain[];
  notes: FeedNote[];
}

// ── Domain stamping (on-disk feeds) ──────────────────────────────────────────
//
// `scanRepo` (the source / library / factory scan path) does not read the
// `DOMAIN.md` sidecars, so its entries arrive with no `domainSlug` and render
// flat under their publisher band. We re-derive the grouping here from the
// root's `DOMAIN.md` files (the same sidecars the remote store computes
// server-side), and fall back to the repo/source name for any entry no domain
// claims so it still groups instead of landing in the synthetic "no domain" pile.

/** Repo-relative POSIX path (DOMAIN.md slugs are matched whole-segment, `/`-joined). */
function toPosix(p: string): string {
  return p.split(path.sep).join("/");
}

/** Parse every `DOMAIN.md` sidecar under `root` into a {@link DomainMd}. */
async function loadDomainMds(root: string): Promise<DomainMd[]> {
  const out: DomainMd[] = [];
  for await (const { path: filePath, name } of walkDir(root)) {
    if (name !== DOMAIN_MD_FILENAME) continue;
    try {
      const { data } = parseFrontmatter(readFileSync(filePath, "utf8"));
      out.push(parseDomainMd(toPosix(path.relative(root, filePath)), data));
    } catch {}
  }
  return out;
}

/**
 * Stamp `domainSlug` onto every entry scanned from `root` using the root's
 * `DOMAIN.md` sidecars. Returns the `CatalogDomain` registry for the real
 * domains, plus a synthetic fallback domain when any entry used the fallback.
 * Mutates `entries` in place (sets `entry.domainSlug`).
 *
 * `synthesizeFallback` (default true): entries no domain claims fall back to
 * `fallbackSlug` (the repo/source name). Pass `false` when the caller already
 * groups these entries one level up (e.g. the source feed groups by source
 * node) — unmatched entries then get no domain (`""`) and hang directly under
 * that parent, instead of nesting under a same-named fallback domain.
 */
async function stampDomains(
  root: string,
  fallbackSlug: string,
  entries: CatalogEntry[],
  synthesizeFallback = true,
): Promise<CatalogDomain[]> {
  const domainMds = await loadDomainMds(root);
  const assignables = entries.map((entry) => ({
    entry,
    ref: `${entry.kind}:${entry.name}`,
    relativePath: toPosix(path.relative(root, entry.source)),
  }));

  let usedFallback = false;
  for (const a of assignables) {
    const slug = assignDomain(a.relativePath, domainMds);
    if (slug) a.entry.domainSlug = slug;
    else if (synthesizeFallback) {
      a.entry.domainSlug = fallbackSlug;
      usedFallback = true;
    } else a.entry.domainSlug = "";
  }

  const { index } = buildDomainIndex(assignables, domainMds);
  const catalog: CatalogDomain[] = Object.values(index).map((d) => ({
    slug: d.slug,
    title: d.title,
    ...(d.description ? { description: d.description } : {}),
    ...(d.relation ? { relation: d.relation } : {}),
    ...(d.order !== undefined ? { order: d.order } : {}),
    ...(d.recommended[0] ? { recommendedRef: d.recommended[0] } : {}),
  }));
  if (usedFallback) catalog.push({ slug: fallbackSlug, title: fallbackSlug });
  return catalog;
}

/**
 * Pull assets from local libraries (the authoring places under
 * `~/.skaile/libraries/<name>/`, not the `~/.skaile/cache/sources/` cache). Each
 * library's `path` is scanned with `scanRepo` and grouped under the bare library
 * name (`group: <name>`) so the manage TUI shows a clean `<name>` band — no
 * `library:` prefix and no redundant same-named fallback domain (mirrors the
 * source feed).
 */
async function libraryAssets(): Promise<{
  entries: OriginEntry[];
  domains: CatalogDomain[];
  notes: FeedNote[];
}> {
  const notes: FeedNote[] = [];
  const domains: CatalogDomain[] = [];
  try {
    const [{ openLibraryManager }, { skaileHomeDir }, { getGlobalCacheDir }] = await Promise.all([
      import("./open-library.ts"),
      import("@skaile/workspaces/library"),
      import("@skaile/workspaces/core"),
    ]);
    const { manager, close } = await openLibraryManager();
    try {
      // Authoring libraries are the rows that are NOT source clones. index.db
      // rows predating the cache migration still carry the legacy
      // ~/.skaile/sources path, so exclude both roots.
      const newRoot = getGlobalCacheDir();
      const legacyRoot = path.join(skaileHomeDir(), "sources");
      const libs = (await manager.listLibraries()).filter(
        (l) => !l.path.startsWith(newRoot) && !l.path.startsWith(legacyRoot),
      );
      const entries: OriginEntry[] = [];
      for (const lib of libs) {
        if (!existsSync(lib.path)) continue;
        // Scan + group under the bare library name. `synthesizeFallback: false`
        // (as the source feed does) keeps domainless assets directly under the
        // library band instead of a redundant same-named fallback domain.
        const libEntries = [...(await scanRepo(lib.path, lib.name))];
        domains.push(...(await stampDomains(lib.path, lib.name, libEntries, false)));
        for (const entry of libEntries) entries.push({ origin: "library", entry, group: lib.name });
      }
      return { entries, domains, notes };
    } finally {
      close();
    }
  } catch (err) {
    notes.push({
      feed: "libraries",
      message: err instanceof Error ? err.message : String(err),
    });
    return { entries: [], domains, notes };
  }
}

/**
 * Pull a (best-effort) snapshot of the connected remote store catalog.
 *
 * - Returns no entries when the resolved catalog is local-only or the host
 *   is unreachable (the failure becomes a note, not an exception).
 * - Maps `CatalogAsset` → `CatalogEntry`, carrying `publisher`, `repo`, and
 *   `category` straight through and using `source = "store://<id>"`; per-file
 *   `source` paths are not available for remote assets so the canonical ref is
 *   a stable placeholder.
 */
async function storeAssets(projectDir: string): Promise<{
  entries: CatalogEntry[];
  domains: CatalogDomain[];
  notes: FeedNote[];
}> {
  const notes: FeedNote[] = [];
  try {
    const { resolveCatalogSource } = await import("./open-library.ts");
    const { source, close } = await resolveCatalogSource({ projectDir });
    try {
      const assets = await source.listAssets();
      const entries: CatalogEntry[] = assets.map((a) => ({
        name: a.name,
        kind: (a.kind as AssetKind) ?? "skill",
        description: a.description ?? "",
        source: `store://${a.id}`,
        publisher: a.publisher,
        repo: a.repo,
        domainSlug: a.domainSlug,
        category: a.category,
        version: a.version,
        requires: [],
        dependencies: [],
      }));
      // Domains are optional on the catalog source — only the store backend
      // (post Part 4 `catalog.listDomains`) exposes them. Duck-type the call so
      // older sources and the local catalog degrade to no domains, not an error.
      const domains = await loadDomains(source);
      return { entries, domains, notes };
    } finally {
      close();
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    // Local-only catalog throws a recognizable message — silence it; absence
    // is the user's choice, not an error.
    if (!/local/i.test(msg)) {
      notes.push({ feed: "store", message: msg });
    }
    return { entries: [], domains: [], notes };
  }
}

/** Optional `listDomains` capability — returns `[]` when the source lacks it. */
async function loadDomains(source: unknown): Promise<CatalogDomain[]> {
  const fn = (source as { listDomains?: () => Promise<CatalogDomain[]> }).listDomains;
  if (typeof fn !== "function") return [];
  const domains = await fn.call(source);
  return Array.isArray(domains) ? domains : [];
}

/** The slice of `AssetManager` the source feed needs (kept narrow for tests). */
interface AssetManagerLike {
  search: () => Promise<CatalogEntry[]>;
  searchRepoRoots: () => { repoName: string; root: string }[];
}

/**
 * Project-source feed: leans on `AssetManager.search()`, which now merges
 * `skaile.yaml`'s `sources:` field into the repositories map, then stamps
 * `DOMAIN.md`-derived domain grouping onto the scanned entries via
 * `searchRepoRoots()`.
 */
async function sourceAssets(
  am: AssetManagerLike,
): Promise<{ entries: OriginEntry[]; domains: CatalogDomain[] }> {
  // The bundled factory tree is injected into the repositories map as an implicit
  // source, so `am.search()` returns it too. It has its own `factory` origin band,
  // so drop it here — otherwise the same first-party skills list twice under Sources.
  const factoryPrefixes = factoryAssetRoots().map((r) => (r.endsWith(path.sep) ? r : r + path.sep));
  const isFactoryPath = (p: string) =>
    factoryPrefixes.some((fp) => p === fp.slice(0, -1) || p.startsWith(fp));

  const all = (await am.search()).filter((e) => !isFactoryPath(e.source));
  const entries: OriginEntry[] = [];
  const domains: CatalogDomain[] = [];
  // Group each source's entries under the source node (its short repo name), not
  // the publisher: under Sources the navigational question is "which repo did
  // this come from". Each entry's `source` (absolute manifest path) lives under
  // exactly one declared root, so group + stamp by prefix. The cache slug is
  // URL-keyed `github.com/<org>/<repo>` — take the trailing segment.
  for (const { repoName, root } of am.searchRepoRoots()) {
    if (isFactoryPath(root)) continue;
    const prefix = root.endsWith(path.sep) ? root : root + path.sep;
    const owned = all.filter((e) => e.source === root || e.source.startsWith(prefix));
    if (owned.length === 0) continue;
    const sourceName = repoName.split("/").pop() || repoName;
    // No synthetic fallback domain — the source node already groups these, so a
    // same-named fallback would just nest the source under itself.
    domains.push(...(await stampDomains(root, sourceName, owned, false)));
    for (const entry of owned) entries.push({ origin: "source", entry, group: sourceName });
  }
  return { entries, domains };
}

/**
 * Factory-asset feed: scan the bundled first-party `factory-assets` tree(s)
 * resolved by `factoryAssetRoots()` (the `SKAILE_FACTORY_ASSETS_DIR` override
 * layer ahead of the bundled tree). Local disk only, no network — the entries are
 * the built-in skills / connectors / bundles that ship with `@skaile/workspaces`.
 * `factoryAssetRoots()` already dedupes, so the env override pointing at the
 * bundled dir collapses to one root.
 */
async function factoryAssets(): Promise<{
  entries: CatalogEntry[];
  domains: CatalogDomain[];
  notes: FeedNote[];
}> {
  const notes: FeedNote[] = [];
  const domains: CatalogDomain[] = [];
  try {
    const entries: CatalogEntry[] = [];
    for (const root of factoryAssetRoots()) {
      const rootEntries = [...(await scanRepo(root, "skaile-factory"))];
      domains.push(...(await stampDomains(root, "skaile-factory", rootEntries)));
      entries.push(...rootEntries);
    }
    return { entries, domains, notes };
  } catch (err) {
    notes.push({
      feed: "factory",
      message: err instanceof Error ? err.message : String(err),
    });
    return { entries: [], domains, notes };
  }
}

/** Tag a flat entry list with a single origin. */
function tag(origin: AssetOrigin, entries: CatalogEntry[]): OriginEntry[] {
  return entries.map((entry) => ({ origin, entry }));
}

/**
 * Run all feeds and return a single normalized result.
 *
 * Feeds run in parallel where possible — the store fetch is the slow one
 * (one HTTP call) and is fired alongside the local library walk. The factory
 * scan is local disk only, so it runs inline.
 */
export async function gatherAssetFeeds(
  am: AssetManagerLike,
  projectDir: string,
): Promise<AssetFeedResult> {
  const [libs, store] = await Promise.all([libraryAssets(), storeAssets(projectDir)]);
  const factory = await factoryAssets();
  const src = await sourceAssets(am);
  return {
    entries: [
      ...src.entries,
      ...libs.entries,
      ...tag("factory", factory.entries),
      ...tag("store", store.entries),
    ],
    // Store domains first (server-curated), then the on-disk feeds; dedupe by
    // slug, first-wins, so a store-served domain shadows a same-slug local one.
    domains: dedupeBySlug([...store.domains, ...src.domains, ...libs.domains, ...factory.domains]),
    notes: [...libs.notes, ...store.notes, ...factory.notes],
  };
}

/** First-wins dedupe of a domain list by `slug`. */
function dedupeBySlug(domains: CatalogDomain[]): CatalogDomain[] {
  const seen = new Set<string>();
  const out: CatalogDomain[] = [];
  for (const d of domains) {
    if (seen.has(d.slug)) continue;
    seen.add(d.slug);
    out.push(d);
  }
  return out;
}
